import { compare, type Comparison } from '../views/sayItBack.ts';
import type { SentenceRef } from './passage.ts';

/**
 * One word of a checked dictation, as shown (listen & write design §3.3).
 * `misheard` is a missed word and an extra one at the same place (§10.7).
 */
export type Part =
  | { kind: 'same'; text: string }
  | { kind: 'missed'; text: string }
  | { kind: 'extra'; text: string }
  | { kind: 'misheard'; target: string; heard: string };

/** `compare`'s own test for a word: a token with no letter or digit is not one. */
function isWord(token: string): boolean {
  return /[\p{L}\p{N}]/u.test(token);
}

/**
 * `compare`'s flags, re-read as an alignment. Its unflagged words are its
 * LCS pairs, so the k-th unflagged word on one side is the k-th on the
 * other; between two such anchors lie the gap's missed and extra words, and
 * pairing them from the gap's start is §10.7's "same position". A word both
 * sides share is never crossed, so a missed word is never paired with an
 * extra one from another part of the sentence.
 *
 * The learner's tokens with no letter or digit are dropped: `compare` never
 * flags them, so they would read as anchors with no partner.
 */
export function pairMisheard(c: Comparison): Part[] {
  const said = c.said.filter((w) => isWord(w.text));
  const parts: Part[] = [];
  let j = 0;
  // The sentence's tokens since the last anchor. A dash or ellipsis in the
  // sentence stays where it is, shown as written and never marked.
  let gap: Array<{ text: string; word: boolean }> = [];
  const flush = (upTo: number) => {
    const extra: string[] = [];
    while (j < upTo) extra.push(said[j++].text);
    let k = 0;
    for (const t of gap) {
      if (!t.word) parts.push({ kind: 'same', text: t.text });
      else if (k < extra.length) parts.push({ kind: 'misheard', target: t.text, heard: extra[k++] });
      else parts.push({ kind: 'missed', text: t.text });
    }
    for (; k < extra.length; k++) parts.push({ kind: 'extra', text: extra[k] });
    gap = [];
  };
  for (const w of c.target) {
    const word = isWord(w.text);
    if (!word || w.differs) {
      gap.push({ text: w.text, word });
      continue;
    }
    let anchor = j;
    while (anchor < said.length && said[anchor].differs) anchor++;
    flush(anchor);
    parts.push({ kind: 'same', text: w.text });
    j = anchor + 1;
  }
  flush(said.length);
  return parts;
}

/** §3.3: each dictated sentence plays up to three times… */
export const MAX_PLAYS = 3;
/** …and the learner may check it, then retry once. */
export const MAX_ATTEMPTS = 2;

/** One dictated sentence's state. Nothing here is scored or shown as a number (§3.3). */
export type Take = {
  readonly plays: number;
  readonly attempts: number;
  readonly typed: string;
  readonly result: Comparison | null;
};

export const FRESH: Take = { plays: 0, attempts: 0, typed: '', result: null };

export function canPlay(t: Take): boolean {
  return t.plays < MAX_PLAYS;
}

export function played(t: Take): Take {
  return canPlay(t) ? { ...t, plays: t.plays + 1 } : t;
}

export function typed(t: Take, text: string): Take {
  return { ...t, typed: text };
}

export function canCheck(t: Take): boolean {
  return t.result === null && t.attempts < MAX_ATTEMPTS && t.typed.trim() !== '';
}

export function check(t: Take, target: string): Take {
  if (!canCheck(t)) return t;
  return { ...t, attempts: t.attempts + 1, result: compare(target, t.typed) };
}

export function canRetry(t: Take): boolean {
  return t.result !== null && t.attempts < MAX_ATTEMPTS;
}

/** The words stay, so a retry is an edit rather than a fresh start. */
export function retry(t: Take): Take {
  return canRetry(t) ? { ...t, result: null } : t;
}

/**
 * `submitRetelling`'s `DictationAttempt`, or null for a sentence never
 * checked: the server accepts a subset of the dictated sentences, each once.
 */
export function attemptOf(
  ref: SentenceRef,
  t: Take,
): { page: number; sentence: number; typed: string; attempts: number } | null {
  return t.attempts === 0 ? null : { page: ref.page, sentence: ref.sentence, typed: t.typed, attempts: t.attempts };
}
